import { designVariables } from "@studio3t/design-variables";
import {
  GenericDropdown,
  type GenericDropdownItem,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import { useCallback } from "react";
import styled from "styled-components";
import {
  DROPDOWN_TRIGGER_ATTR,
  DropdownOpenReporter,
} from "@/components/dropdownDismissGuard";
import { useToast } from "@/components/toast/ToastProvider";
import type { JobMetadata } from "@/types/api";
import { deriveJobControlState } from "./pipelineControls";
import { type NormalizedJobState, normalizeState } from "./pipelineHelpers";

const BUTTON = designVariables.ui.button;

// Shared by the Pipelines and Workspaces actions columns.
export const ActionsTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${BUTTON.s.size}px;
  padding: 0 ${BUTTON.s.paddingw}px;
  border: none;
  border-radius: ${BUTTON.corner}px;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${(props) => props.theme.sidebarVerticalTabsItemHoverBackground};
  }

  /* A global reset strips button outlines; restore the browser-default
     ring for keyboard focus. The explicit ring color is needed because
     "outline: auto" alone would fall back to currentColor (dark gray). */
  &:focus-visible {
    outline: 5px auto -webkit-focus-ring-color;
  }
`;

// Error-handling wrapper for a job mutation (stop/restart): a failure shows
// as an error toast. The mutations invalidate the jobs query and useJobs
// polls, so the list refreshes without a manual refetch here.
export function useJobAction(
  action: (name: string) => Promise<unknown>,
  fallbackMessage: string,
) {
  const { toast } = useToast();
  const execute = useCallback(
    async (name: string) => {
      try {
        await action(name);
      } catch (err) {
        toast({
          state: "Error",
          message: err instanceof Error ? err.message : fallbackMessage,
        });
      }
    },
    [action, fallbackMessage, toast],
  );
  return { execute };
}

interface JobActionsProps {
  job: JobMetadata;
  state: NormalizedJobState;
  onStop: (name: string) => void;
  onRestart: (name: string) => void;
  onDelete: (name: string) => void;
  onImport: (job: JobMetadata) => void;
  onDuplicate: (job: JobMetadata) => void;
  /** The menu's open state, for the table's dropdownDismissGuard. */
  onMenuOpenChange?: (open: boolean) => void;
  stopping: boolean;
  restarting: boolean;
  deleting: boolean;
  importing: boolean;
}

// A stopped pipeline opens editable in the Studio; a running one can only
// be viewed there (Figma 14894-157270).
function openInStudioItem(
  editable: boolean,
  importing: boolean,
  onClick: () => void,
): ListItemProps {
  return {
    id: "open",
    type: "text",
    label: editable ? "Edit in Studio" : "View in Studio",
    value: "open",
    leadingIcon: editable ? Icon.Edit : Icon.Show,
    disabled: importing,
    onClick,
  };
}

function duplicateItem(onClick: () => void): ListItemProps {
  return {
    id: "duplicate",
    type: "text",
    label: "Duplicate as draft",
    value: "duplicate",
    leadingIcon: Icon.Duplicate,
    closeOnSelect: true,
    onClick,
  };
}

// The per-row actions menu. Running (Figma 14894-157270): Stop, View in
// Studio, Duplicate as draft. Stopped or failed (14894-157281): Start, Edit
// in Studio, Duplicate as draft, then Delete behind a separator. Start/Stop is driven off the
// state↔desired_state divergence so it stays disabled until the backend
// converges (see deriveJobControlState).
export function JobActions({
  job,
  state,
  onStop,
  onRestart,
  onDelete,
  onImport,
  onDuplicate,
  onMenuOpenChange,
  stopping,
  restarting,
  deleting,
  importing,
}: JobActionsProps) {
  const desiredState = normalizeState(job.desired_state);
  const { startDisabled, stopDisabled, displayState } = deriveJobControlState(
    state,
    desiredState,
    { starting: restarting, stopping },
  );
  const isTransitioning =
    displayState === "Starting" || displayState === "Stopping";
  // A failed job offers Start (retry); otherwise the action follows the job's
  // target state, so a converging start still shows a (disabled) Stop.
  const showStop = state !== "Failed" && desiredState === "Running";
  const isStopped =
    (state === "Stopped" || state === "Failed") && !isTransitioning;

  const main: ListItemProps[] = [
    showStop
      ? {
          id: "stop",
          type: "text",
          label: "Stop",
          value: "stop",
          leadingIcon: Icon.Stop,
          disabled: stopDisabled,
          onClick: () => onStop(job.name),
        }
      : {
          id: "start",
          type: "text",
          label: "Start",
          value: "start",
          leadingIcon: Icon.Run,
          disabled: startDisabled,
          onClick: () => onRestart(job.name),
        },
    openInStudioItem(isStopped, importing, () => onImport(job)),
    duplicateItem(() => onDuplicate(job)),
  ];

  const items: GenericDropdownItem[] = [main];
  if (isStopped) {
    items.push([
      {
        id: "delete",
        type: "text",
        label: "Delete",
        // Label stays the default (black) like every other item; only the bin
        // icon is red. error: true would render the item non-interactive
        // (ListItem's handleOnClick bails on disabled || error), so the red is
        // applied via leadingIconColorKey alone.
        value: "delete",
        leadingIcon: Icon.Bin,
        leadingIconColorKey: SVGColorKey.errorIcon,
        disabled: deleting,
        closeOnSelect: true,
        onClick: () => onDelete(job.name),
      },
    ]);
  }

  return (
    <GenericDropdown
      dropdownItems={items}
      width={{ strategy: "fixed", size: 202 }}
      placement="bottom-end"
    >
      <ActionsTrigger
        type="button"
        aria-label={`Actions for ${job.name}`}
        {...DROPDOWN_TRIGGER_ATTR}
      >
        <DropdownOpenReporter onOpenChange={onMenuOpenChange} />
        <IconSVG
          icon={Icon.MoreH}
          size={BUTTON.iconsize}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </ActionsTrigger>
    </GenericDropdown>
  );
}
